import React, { useState, useEffect } from 'react';
import { useMixingAndBakingContext, MB_SCREENS } from '../context/MixingAndBakingContext';
import type { MBSessionData } from '../context/MixingAndBakingContext';
import { mixingAndBakingService } from '../services/mixingAndBakingService';
import { getProductList, applyRecipeToCycleRecord } from '../utils/recipeUtils';
import { useMsal } from "@azure/msal-react";
import { SP_MSAL_SCOPE } from '../../../../constants/sharepoint';
import moment from 'moment';

const ChecklistInformation: React.FC = () => {
    const { state, dispatch } = useMixingAndBakingContext();
    const { instance, accounts } = useMsal();

    const [site, setSite] = useState<string>(state.sessionData?.site || "Rajpura");
    const [line, setLine] = useState<string>(state.sessionData?.line || "Line 1");
    const [shift, setShift] = useState<string>(state.sessionData?.shift || sessionStorage.getItem("shiftValue") || "Shift 1");
    const [productName, setProductName] = useState<string>(state.sessionData?.productName || "");
    const [qaExecutive, setQaExecutive] = useState<string>(state.sessionData?.qaExecutive || "");
    const [productionIncharge, setProductionIncharge] = useState<string>(state.sessionData?.productionIncharge || "");
    const [batchNo, setBatchNo] = useState<string>(state.sessionData?.batchNo || "");
    const [dateStr] = useState<string>(moment().format("DD/MM/YYYY"));

    const [qaOptions, setQaOptions] = useState<string[]>([]);
    const [prodOptions, setProdOptions] = useState<string[]>([]);
    const [loadingConfig, setLoadingConfig] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const productOptions = getProductList();

    // Resolve shift executive name (logged-in user)
    const shiftExec = accounts && accounts[0]?.name ? accounts[0].name : "Shift Executive";

    // Lines based on site
    const lines = site === "Rajpura"
        ? ["Line 1", "Line 2", "Line 3", "Line 4", "Line 5", "Line 6", "Line 7", "Line 8"]
        : ["Line 1", "Line 2", "Line 3", "Line 4", "Line 5"];

    useEffect(() => {
        if (!lines.includes(line)) {
            setLine(lines[0]);
        }
    }, [site]);

    // Load QA and Production personnel from SharePoint
    useEffect(() => {
        let isMounted = true;
        const loadConfig = async () => {
            setLoadingConfig(true);
            try {
                let spToken = "";
                if (accounts && accounts.length > 0) {
                    try {
                        const tokenResponse = await instance.acquireTokenSilent({
                            scopes: [SP_MSAL_SCOPE],
                            account: accounts[0]
                        });
                        spToken = tokenResponse.accessToken;
                    } catch (e) {
                        console.warn("Silent SharePoint token acquisition failed:", e);
                    }
                }

                const configs = await mixingAndBakingService.getConfig(spToken);
                if (isMounted && configs && Array.isArray(configs)) {
                    const qas = Array.from(new Set(configs.map((c: any) => c.qaExecutive).filter(Boolean))) as string[];
                    const prods = Array.from(new Set(configs.map((c: any) => c.productionIncharge).filter(Boolean))) as string[];

                    setQaOptions(qas);
                    setProdOptions(prods);

                    if (qas.length > 0 && !qaExecutive) {
                        setQaExecutive(state.sessionData?.qaExecutive || qas[0]);
                    }
                    if (prods.length > 0 && !productionIncharge) {
                        setProductionIncharge(state.sessionData?.productionIncharge || prods[0]);
                    }
                }
            } catch (err) {
                console.error("Error loading config:", err);
            } finally {
                if (isMounted) setLoadingConfig(false);
            }
        };

        loadConfig();
        return () => { isMounted = false; };
    }, [instance, accounts]);

    const handleStartSession = async () => {
        setErrorMsg(null);

        if (!site || !line || !productName || !qaExecutive || !productionIncharge || !shift || !batchNo.trim()) {
            setErrorMsg("Please fill in all required fields marked with *.");
            return;
        }

        dispatch({ type: 'SET_LOADING', payload: true });
        try {
            const sessionPayload: MBSessionData = {
                site,
                line,
                shift,
                qaExecutive: qaExecutive.trim(),
                productionIncharge: productionIncharge.trim(),
                productName: productName.trim(),
                executiveName: shiftExec,
                batchNo: batchNo.trim(),
                cycle: state.currentCycle || "Cycle-1",
                typeOfMixer: state.sessionData?.typeOfMixer || "Vertical",
                tourStartDate: state.sessionData?.tourStartDate || new Date().toISOString()
            };

            // Save parent tour to Dataverse / IndexedDB
            const tourId = await mixingAndBakingService.createOrGetTour(sessionPayload);
            if (tourId) {
                dispatch({ type: 'SET_TOUR_ID', payload: tourId });
            }

            dispatch({ type: 'SET_SESSION_DATA', payload: sessionPayload });

            // Auto-populate standards for active cycle based on product recipe
            const updatedActive = applyRecipeToCycleRecord({
                ...state.activeCycleRecord,
                productName: productName.trim(),
                batchNo: batchNo.trim(),
                lineNo: line,
                shift: shift,
                observedBy: qaExecutive.trim(),
                productionIncharge: productionIncharge.trim(),
                executiveName: shiftExec
            }, productName.trim());

            dispatch({ type: 'SET_ACTIVE_CYCLE_RECORD', payload: updatedActive });
            dispatch({ type: 'SET_SCREEN', payload: MB_SCREENS.CYCLE_ENTRY });

        } catch (err: any) {
            console.error("Failed to start session:", err);
            setErrorMsg(err.message || "Failed to start session. Please try again.");
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <div id="screen-checklist-info" style={{ display: 'block' }}>
            {/* Stepper Progress Bar */}
            <div className="tour-workflow-stepper" style={{ marginBottom: '20px', background: '#f8fafc', border: '1px solid #e2e8f0', padding: '12px 18px', borderRadius: '8px', fontSize: '13px', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div className="breadcrumb-step active" style={{ display: 'inline-flex', alignItems: 'center', backgroundColor: '#2563eb', color: '#ffffff', padding: '5px 12px', borderRadius: '6px', fontWeight: 700, boxShadow: '0 1px 2px rgba(37,99,235,0.2)' }}>
                        <span style={{ marginRight: '5px' }}>1.</span>
                        <span>Request Info</span>
                    </div>
                    <span style={{ color: '#cbd5e1', fontWeight: 'bold', fontSize: '14px' }}>&gt;</span>
                    <div className="breadcrumb-step" style={{ display: 'inline-flex', alignItems: 'center', color: '#64748b', padding: '5px 12px', borderRadius: '6px', fontWeight: 500 }}>
                        <span style={{ marginRight: '5px' }}>2.</span>
                        <span>Process Checklists</span>
                    </div>
                    <span style={{ color: '#cbd5e1', fontWeight: 'bold', fontSize: '14px' }}>&gt;</span>
                    <div className="breadcrumb-step" style={{ display: 'inline-flex', alignItems: 'center', color: '#64748b', padding: '5px 12px', borderRadius: '6px', fontWeight: 500 }}>
                        <span style={{ marginRight: '5px' }}>3.</span>
                        <span>Completed</span>
                    </div>
                </div>
            </div>

            <div className="bs-card" style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}>
                <div className="bs-card-header" style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                    <h4 className="bs-card-title" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>Checklist Information</h4>
                </div>
                <div className="bs-card-body" style={{ padding: '20px' }}>
                    {errorMsg && (
                        <div style={{ backgroundColor: '#fee2e2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px' }}>
                            <strong>Error: </strong> {errorMsg}
                        </div>
                    )}

                    <div id="checklist-info-form" className="tour-cyle-info-form">
                        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
                            {/* Manufacturing Site */}
                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    Manufacturing Site <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div className="select2-parent">
                                    <select
                                        id="info-site-select"
                                        className="form-select"
                                        value={site}
                                        onChange={(e) => setSite(e.target.value)}
                                        style={{ width: '100%', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                    >
                                        <option value="Rajpura">Rajpura</option>
                                        <option value="Phillaur">Phillaur</option>
                                        <option value="Indore">Indore</option>
                                        <option value="Tahliwal">Tahliwal</option>
                                    </select>
                                </div>
                            </div>

                            {/* Line No */}
                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    Line No <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div className="select2-parent">
                                    <select
                                        id="info-line-select"
                                        className="form-select"
                                        value={line}
                                        onChange={(e) => setLine(e.target.value)}
                                        style={{ width: '100%', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                    >
                                        {lines.map((l) => (
                                            <option key={l} value={l}>{l}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Product Name */}
                            <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    Product Name <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div className="select2-parent">
                                    <select
                                        id="info-product-select"
                                        className="form-select"
                                        value={productName}
                                        onChange={(e) => setProductName(e.target.value)}
                                        style={{ width: '100%', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                    >
                                        <option value="">Select Product</option>
                                        {productOptions.map((p, idx) => (
                                            <option key={`${p.displayTitle}-${idx}`} value={p.displayTitle}>
                                                {p.displayTitle}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* QA Executive Name */}
                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    QA Executive Name <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div className="select2-parent">
                                    <select
                                        id="info-qa-select"
                                        className="form-select"
                                        value={qaExecutive}
                                        onChange={(e) => setQaExecutive(e.target.value)}
                                        style={{ width: '100%', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                    >
                                        <option value="">{loadingConfig ? "Loading QA Executives..." : "Select QA Executive"}</option>
                                        {qaOptions.map((qa) => (
                                            <option key={qa} value={qa}>{qa}</option>
                                        ))}
                                        {/* Fallback option if custom/current */}
                                        {qaExecutive && !qaOptions.includes(qaExecutive) && (
                                            <option value={qaExecutive}>{qaExecutive}</option>
                                        )}
                                    </select>
                                </div>
                            </div>

                            {/* Production Executive */}
                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    Production Executive <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div className="select2-parent">
                                    <select
                                        id="info-prod-select"
                                        className="form-select"
                                        value={productionIncharge}
                                        onChange={(e) => setProductionIncharge(e.target.value)}
                                        style={{ width: '100%', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                    >
                                        <option value="">{loadingConfig ? "Loading Production Executives..." : "Select Production Executive"}</option>
                                        {prodOptions.map((pr) => (
                                            <option key={pr} value={pr}>{pr}</option>
                                        ))}
                                        {productionIncharge && !prodOptions.includes(productionIncharge) && (
                                            <option value={productionIncharge}>{productionIncharge}</option>
                                        )}
                                    </select>
                                </div>
                            </div>

                            {/* Shift Executive (logged-in user who starts tour) */}
                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    Shift Executive
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="info-shift-exec"
                                    value={shiftExec}
                                    disabled
                                    readOnly
                                    style={{ backgroundColor: '#f8fafc', color: '#334155', fontWeight: 600, height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                />
                            </div>

                            {/* Shift */}
                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    Shift <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <div className="select2-parent">
                                    <select
                                        id="info-shift-select"
                                        className="form-select"
                                        value={shift}
                                        onChange={(e) => setShift(e.target.value)}
                                        style={{ width: '100%', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                    >
                                        <option value="Shift 1">Shift 1</option>
                                        <option value="Shift 2">Shift 2</option>
                                        <option value="Shift 3">Shift 3</option>
                                    </select>
                                </div>
                            </div>

                            {/* Batch No */}
                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    Batch No <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="info-batch-input"
                                    placeholder="Enter Batch No (e.g. B-101)"
                                    value={batchNo}
                                    onChange={(e) => setBatchNo(e.target.value)}
                                    style={{ width: '100%', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                />
                            </div>

                            {/* Date */}
                            <div className="form-group">
                                <label className="form-label" style={{ fontWeight: 600, fontSize: '13px', marginBottom: '6px', display: 'block' }}>
                                    Date
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="info-date-input"
                                    value={dateStr}
                                    disabled
                                    readOnly
                                    style={{ background: '#f8fafc', width: '100%', height: '38px', borderRadius: '6px', border: '1px solid #cbd5e1', padding: '6px 12px' }}
                                />
                            </div>
                        </div>

                        <div className="form-footer" style={{ marginTop: '25px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className="bs-btn bs-btn-primary"
                                style={{ padding: '10px 28px', fontWeight: 700, fontSize: '14px', borderRadius: '8px', backgroundColor: '#2563eb', color: '#ffffff', border: 'none', cursor: 'pointer' }}
                                onClick={handleStartSession}
                            >
                                Start Session
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChecklistInformation;
